import type { EdoRequest } from '../../lib/types'

/** Empty strings explicitly clear optional draft fields; omission preserves them. */
export interface TerminationInput {
  terminationDate?: string
  terminationReasonId?: string
  reason?: string
}
export interface TerminationDraftInput extends TerminationInput { employmentId?: string }
export interface TerminationReferences {
  companyId: string
  source: string
  employeeId: string
  employeeName: string
  /** name — код 1С (уходит в ссылку), text — формулировка статьи из 1С. */
  reasons: { id: string; name: string; text?: string }[]
  /** Основание по умолчанию — ст. 69.1 (сотрудник его не выбирает, ответ аналитика 07.10.2026). */
  basis?: { id: string; name: string; text?: string } | null
  /** Самая ранняя дата увольнения для новой заявки: месяц от сегодняшней подачи. */
  earliestDate?: string
  blockers: string[]
  canSubmit: boolean
}

/** Причины ухода для Exit-интервью — зеркало бэкендового EXIT_REASONS (termination-route.ts). */
export const EXIT_REASONS = [
  { value: 'salary', label: { ru: 'Заработная плата', en: 'Salary', az: 'Əmək haqqı' } },
  { value: 'career', label: { ru: 'Карьерный рост и развитие', en: 'Career growth', az: 'Karyera inkişafı' } },
  { value: 'manager', label: { ru: 'Отношения с руководителем', en: 'Relations with manager', az: 'Rəhbərlə münasibət' } },
  { value: 'conditions', label: { ru: 'Условия труда и нагрузка', en: 'Working conditions and workload', az: 'İş şəraiti və yüklənmə' } },
  { value: 'relocation_family', label: { ru: 'Переезд или семейные обстоятельства', en: 'Relocation or family', az: 'Köçmə və ya ailə vəziyyəti' } },
  { value: 'other_employer', label: { ru: 'Переход к другому работодателю', en: 'Another employer', az: 'Başqa işəgötürənə keçid' } },
  { value: 'study', label: { ru: 'Учёба', en: 'Study', az: 'Təhsil' } },
  { value: 'other', label: { ru: 'Другое', en: 'Other', az: 'Digər' } },
] as const
export type ExitReason = (typeof EXIT_REASONS)[number]['value']
export interface ExitInterviewInput {
  refused?: boolean
  reason?: ExitReason
  comment?: string
  uploadId?: string
}
/** Результаты интервью в карточке — сервер отдаёт их только HR компании, CEO и admin. */
export interface ExitInterviewView {
  refused: boolean
  reason: ExitReason | null
  comment: string | null
  createdAt: string
  by?: { id: string; fullName: string } | null
}
export function isPreparedTermination(card: Pick<EdoRequest, 'type' | 'payload'>): boolean {
  return card.type === 'TERMINATION' && (card.payload as unknown as { terminationContractVersion?: string }).terminationContractVersion === '2026-10-05'
}
/** Неподанный черновик увольнения: маршрута ещё нет (после подачи он виден как у прочих процессов). */
export function isLocalTerminationDraft(card: Pick<EdoRequest, 'type' | 'payload' | 'status'>): boolean {
  return card.status === 'DRAFT' && isPreparedTermination(card)
}
export function canEditTerminationDraft(card: EdoRequest, userId?: string): boolean {
  return !!userId && (card.initiator?.id ?? card.initiatorId) === userId && card.type === 'TERMINATION' &&
    card.status === 'DRAFT' && isPreparedTermination(card) &&
    !card.isSigned && !card.processInstanceId && !Object.keys(card.result ?? {}).length
}
